import React, { useEffect, useRef, useState } from "react";
import { MuiOtpInput } from "mui-one-time-password-input";
import { Box, CircularProgress, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import authentication from "../services/AmplifyServices";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AmplifyEnv from "../constants/AmplifyEnv";

const easing = [0.6, -0.05, 0.01, 0.99];
const animate = { opacity: 1, y: 0, transition: { duration: 0.6, ease: easing, delay: 0.16 } };

const errorToast = (err) => {
  const msg = err?.message || "";
  if (msg.includes("RECAPTCHA_EXPIRED"))
    return toast.warn("Verification expired. Please try again.", { toastId: "otp-expired" });
  if (msg.includes("reCAPTCHA verification failed") || msg.includes("Missing reCAPTCHA token"))
    return toast.error("Something didn't look right. Please try again in a moment.", { toastId: "otp-bot" });
  if (msg.includes("RECAPTCHA_TIMEOUT") || msg.includes("aborted due to timeout"))
    return toast.error("Verification timed out. Please try again.", { toastId: "otp-timeout" });
  toast("Please enter the right code.", { toastId: "otp-wrong" });
};

const OTPInner = ({ setAuth }) => {
  const [otp, setOTP] = useState("");
  const [verifying, setVerifying] = useState(false);
  const submitting = useRef(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleChange = async (newValue) => {
    if (submitting.current) return;
    setOTP(newValue);
    if (!/^[0-9]{4}$/.test(newValue)) return;

    submitting.current = true;
    setVerifying(true);
    try {
      if (!executeRecaptcha) throw new Error("reCAPTCHA verification failed");
      const recaptchaToken = await executeRecaptcha("otp");
      const { idToken, refreshToken } = await authentication.answerCustomChallenge(newValue, recaptchaToken);

      setAuth(true);
      const redirectURL = `${localStorage.getItem("redirectURL")}?idToken=${idToken.getJwtToken()}&refreshToken=${refreshToken.getToken()}`;
      localStorage.removeItem("redirectURL");
      window.location = redirectURL; // overlay stays until the page leaves
    } catch (err) {
      console.error("OTP submit failed:", err);
      errorToast(err);
      setOTP("");
      setVerifying(false);
      submitting.current = false;
    }
  };

  const [isDark, setIsDark] = useState(document.documentElement.getAttribute("data-theme") === "dark");
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setIsDark(el.getAttribute("data-theme") === "dark"));
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  return (
    <Box component={motion.div} sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "3rem", my: 2 }} animate={{ transition: { staggerChildren: 0.55 } }}>
      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: 3 }} component={motion.div} initial={{ opacity: 0, y: 40 }} animate={animate}>
        <MuiOtpInput
          value={otp}
          onChange={handleChange}
          autoFocus
          TextFieldsProps={{
            type: "number",
            disabled: verifying,
            inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
            size: "medium",
            sx: {
              "& .MuiOutlinedInput-root": { borderRadius: "25px", backgroundColor: isDark ? "#696969" : "#F4F4F4", ...(isDark ? { color: "#FFF" } : {}) },
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            },
          }}
        />

        {verifying && (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              borderRadius: "25px",
              backdropFilter: "blur(2px)",
              backgroundColor: isDark ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.7)",
            }}
          >
            <CircularProgress size={22} sx={{ color: isDark ? "#FFF" : "#000" }} />
            <Typography variant="body2" sx={{ color: isDark ? "#FFF" : "#000" }}>Verifying…</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const OTPForm = (props) => (
  <GoogleReCaptchaProvider reCaptchaKey={AmplifyEnv.G_RECAPTCHA_V3_SITE_KEY}>
    <OTPInner {...props} />
  </GoogleReCaptchaProvider>
);

export default OTPForm;